import React, { useEffect, useState, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Button, message } from 'antd'
import { UploadOutlined } from '@ant-design/icons'
import { Breadcrumbs } from '../../../components'
import UploadFormModal from '../../../forms/UploadFormModal'
import { selectAllVideos } from '../../../redux/features/videos/videoSelectors'
import { deleteVideoThunk } from '../../../redux/features/videos/videoThunk'
import { ViewContentModal } from '../../../components/ViewContentModal'
import { ContentTable } from '../../../components/ContentTable'
import { ContentFilterBar } from '../../../components/ContentFilterBar'
import { BulkActionsBar } from '../../../components/BulkActionsBar'

interface DataType {
  key: string
  id: string
  title: string
  category: string
  description: string
  thumbnail: string
  url: string
  likes?: number
  views?: number
  createdAt?: string
}

const VideosPage = () => {
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isUploadFormModalVisible, setIsUploadFormModalVisible] = useState(false)
  const [viewContent, setViewContent] = useState<DataType | null>(null)
  const [editData, setEditData] = useState<any>(null)
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [selectedRows, setSelectedRows] = useState<DataType[]>([])

  // Filter state
  const [searchQuery, setSearchQuery] = useState('')
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(undefined)

  const videos = useSelector(selectAllVideos)
  const dispatch = useDispatch<any>()

  // Transform video data
  const transformedVideoData: DataType[] = useMemo(() => {
    return videos.map((video) => ({
      key: video?.id,
      id: video?.id,
      title: video?.title || '',
      category: video?.category || '',
      description: video?.description || '',
      thumbnail: video?.thumbnail || '',
      url: video?.url || '',
      likes: video?.likes || 0,
      views: (video as any)?.views || 0,
      createdAt: (video as any)?.createdAt,
    }))
  }, [videos])

  // Get unique categories for filter
  const categories = useMemo(() => {
    const cats = transformedVideoData
      .map((v) => v.category)
      .filter((c) => c && c.trim() !== '')
    return [...new Set(cats)]
  }, [transformedVideoData])

  // Filtered data
  const filteredData = useMemo(() => {
    let result = transformedVideoData

    if (searchQuery) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (item) =>
          item.title.toLowerCase().includes(query) ||
          item.description.toLowerCase().includes(query)
      )
    }

    if (categoryFilter) {
      result = result.filter((item) => item.category === categoryFilter)
    }

    return result
  }, [transformedVideoData, searchQuery, categoryFilter])

  const handleView = (record: DataType) => {
    setViewContent(record)
    setIsViewModalVisible(true)
  }

  const handleEdit = (record: DataType) => {
    setEditData({
      id: record.id,
      title: record.title,
      description: record.description,
      category: record.category,
      thumbnail: record.thumbnail,
      url: record.url,
    })
    setIsUploadFormModalVisible(true)
  }

  const handleDelete = (record: DataType) => {
    dispatch(deleteVideoThunk(record.id))
    message.success('Video deleted successfully')
  }

  const handleBulkDelete = () => {
    selectedRows.forEach((record) => {
      dispatch(deleteVideoThunk(record.id))
    })
    message.success(`${selectedRows.length} videos deleted successfully`)
    setSelectedRowKeys([])
    setSelectedRows([])
  }

  const handleSelectionChange = (keys: React.Key[], records: DataType[]) => {
    setSelectedRowKeys(keys)
    setSelectedRows(records)
  }

  const handleViewModalClose = () => {
    setIsViewModalVisible(false)
    setViewContent(null)
  }

  const handleSwitchToEdit = () => {
    if (viewContent) {
      handleEdit(viewContent)
      handleViewModalClose()
    }
  }

  const showUploadFormModal = () => {
    setEditData(null)
    setIsUploadFormModalVisible(true)
  }

  const handleCancelUploadFormModal = () => {
    setIsUploadFormModalVisible(false)
    setEditData(null)
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setCategoryFilter(undefined)
  }

  return (
    <div>
      <Breadcrumbs />

      {/* Upload Button */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '16px 20px',
          marginBottom: '16px',
          background: 'linear-gradient(135deg, #e6f7ff 0%, #f0f5ff 100%)',
          borderRadius: '10px',
          border: '1px solid #91d5ff',
        }}
      >
        <div>
          <h3 style={{ margin: 0, color: '#1890ff' }}>Videos</h3>
          <span style={{ color: '#666' }}>{filteredData.length} videos</span>
        </div>
        <Button type="primary" icon={<UploadOutlined />} onClick={showUploadFormModal}>
          Upload Video
        </Button>
      </div>

      {/* Filter Bar */}
      <ContentFilterBar
        onSearch={setSearchQuery}
        onCategoryChange={setCategoryFilter}
        onClear={handleClearFilters}
        searchValue={searchQuery}
        categoryValue={categoryFilter}
        categories={categories}
        placeholder="Search videos..."
      />

      {/* Bulk Actions Bar */}
      <BulkActionsBar
        selectedCount={selectedRowKeys.length}
        onBulkDelete={handleBulkDelete}
        onClearSelection={() => {
          setSelectedRowKeys([])
          setSelectedRows([])
        }}
        itemType="video"
      />

      {/* Content Table */}
      <ContentTable
        dataSource={filteredData}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        selectedRowKeys={selectedRowKeys}
        onSelectionChange={handleSelectionChange}
        type="video"
      />

      {/* View Modal */}
      {viewContent && (
        <ViewContentModal
          isVisible={isViewModalVisible}
          onClose={handleViewModalClose}
          onEdit={handleSwitchToEdit}
          type="video"
          content={viewContent}
        />
      )}

      {/* Upload/Edit Modal */}
      <UploadFormModal
        visible={isUploadFormModalVisible}
        onClose={handleCancelUploadFormModal}
        label="video"
        editData={editData}
      />
    </div>
  )
}

export { VideosPage }
